import React, { useEffect, useRef, useState } from "react";
import { Column, Loader } from "@amzn/meridian";
import "src/components/pages/inspection/cameraFrame.scss";
import { CaptureMode, captureTimingLogger } from "src/services/captureTimingLogger";
import StreamingDevControls from "./streamingDevControls";
import DetectionOverlay from "./detectionOverlay";
import GuidanceMessageOverlay from "./components/GuidanceMessageOverlay";
import CameraControlsButton from "./components/CameraControlsButton";
import CaptureButton from "./components/CaptureButton";
import FlashlightButton from "./components/FlashlightButton";
import ZoomControl from "./components/ZoomControl";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { analyzeEvidenceAsync } from "src/store/inspectionResultsSlice";
import { useStreamingService } from "./hooks/useStreamingService";
import { CameraStreamProps } from "src/components/pages/configurationDrivenInspections/types/cameraStreamProps";

type CameraFrameProps = {
  onCapture: () => void;
  targetClasses: string[];
  activeQuestionIds: string[];
  cameraStreamProps: CameraStreamProps;
};

const CameraFrame = ({
  onCapture,
  targetClasses,
  activeQuestionIds,
  cameraStreamProps,
}: CameraFrameProps) => {
  const dispatch = useAppDispatch();
  const {
    streamingDevMode,
    streamingConnected,
    detectionBoxes,
    imageNextStage,
    scannedBarcode,
    streamingMetrics,
  } = useAppSelector((state) => state.inspectionCapture);
  
  const [ showCameraControls, setShowCameraControls ] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const { inspectionId, currentIterationIndex, totalIterations } = useAppSelector((state) => state.workflow);
  const { activeSiteCode } = useAppSelector((state) => state.user);
  
  const { lowResVideoRef, highResVideoRef, captureHighResFrame, streamsReady } = cameraStreamProps;

  // Resolve container vs label from the workflow target classes for the
  // timing dimension.
  const objectType = captureTimingLogger.resolveObjectType(targetClasses);

  // The auto-capture success callback (onReadyForAnalyzer) is captured once by
  // useStreamingService's effect and does NOT re-run on every render, so it
  // would otherwise read a stale snapshot of streamingMetrics / workflow state.
  // Keep a live ref, updated after every render, so the callback reads the
  // freshest values at the moment the capture actually succeeds.
  const successContextRef = useRef({
    streamingMetrics,
    scannedBarcode,
    inspectionId,
    currentIterationIndex,
    totalIterations,
    objectType,
    activeSiteCode,
  });
  useEffect(() => {
    successContextRef.current = {
      streamingMetrics,
      scannedBarcode,
      inspectionId,
      currentIterationIndex,
      totalIterations,
      objectType,
      activeSiteCode,
    };
  });

  // Build the timing-log params from the live ref so both the auto (stale
  // closure) and manual paths report the same, current values.
  const buildSuccessParams = (captureMode: CaptureMode) => {
    const ctx = successContextRef.current;
    const metrics = ctx.streamingMetrics;
    const sampleCount = metrics.latencies.length; // how many WebSocket round-trip latency measurements were collected during that capture session
    const hasSamples = sampleCount > 0;
    const lowCount = metrics.lowResLatencies.length;
    const highCount = metrics.highResLatencies.length;
    return {
      captureMode,
      objectType: ctx.objectType,
      siteCode: ctx.activeSiteCode || null,
      inspectionId: ctx.inspectionId || null,
      containerId: ctx.scannedBarcode ?? null,
      containerIndex: ctx.totalIterations > 0 ? ctx.currentIterationIndex + 1 : null,
      // The container loop is open-ended (the inspector ends it manually), so
      // there is no known total. `totalIterations` now only tracks the count
      // reached so far and would just mirror `containerIndex`, so report null.
      totalContainers: null,
      // Overall (all frames, including low-res and hight res). Latency is null (not 0) when nothing was measured.
      avgLatencyMs: hasSamples ? metrics.averageLatency : null,
      p50LatencyMs: hasSamples ? metrics.p50Latency : null,
      p90LatencyMs: hasSamples ? metrics.p90Latency : null,
      latencySampleCount: sampleCount,
      avgImageSizeKB: metrics.averageImageSize,
      lastImageSizeKB: metrics.currentImageSize,
      // Low-res streaming frames
      lowResAvgLatencyMs: lowCount > 0 ? metrics.lowResAverageLatency : null,
      lowResP50LatencyMs: lowCount > 0 ? metrics.lowResP50Latency : null,
      lowResP90LatencyMs: lowCount > 0 ? metrics.lowResP90Latency : null,
      lowResLatencySampleCount: lowCount,
      lowResAvgImageSizeKB: lowCount > 0 ? metrics.lowResAverageImageSize : null,
      // High-res capture frames
      highResAvgLatencyMs: highCount > 0 ? metrics.highResAverageLatency : null,
      highResP50LatencyMs: highCount > 0 ? metrics.highResP50Latency : null,
      highResP90LatencyMs: highCount > 0 ? metrics.highResP90Latency : null,
      highResLatencySampleCount: highCount,
      highResAvgImageSizeKB: highCount > 0 ? metrics.highResAverageImageSize : null,
    };
  };

  // Start the user-perceived timer when the camera stream becomes ready for
  // this detect-object step. Keyed on step identity so each container/label
  // capture is timed independently. (It's a useEffect deendency that controls
  // the timer restarts)
  const stepKey = `${targetClasses.join(',')}-${activeQuestionIds.join(',')}`;
  useEffect(() => {
    if (streamsReady) {
      captureTimingLogger.start(objectType);
    }
    return () => {
      captureTimingLogger.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamsReady, stepKey]);

  const { streamingService, pauseStreaming, resumeStreaming } = useStreamingService({
    lowResVideoRef,
    highResVideoRef,
    enabled: streamsReady,
    onReadyForAnalyzer: () => {
      captureTimingLogger.recordSuccess(buildSuccessParams("auto"));
      setShowCameraControls(false);
      onCapture();
    },
    // Mark the low-res → high-res boundary so the timing logger can split
    // totalMs into positioning (low-res) vs high-res confirm time.
    onHighRes: () => {
      captureTimingLogger.markHighResTransition();
    },
    containerId: scannedBarcode || undefined,
    targetClasses: targetClasses,
    activeQuestionIds,
  });
  
  // Video dimensions
  const videoWidth = lowResVideoRef.current?.videoWidth || 0;
  const videoHeight = lowResVideoRef.current?.videoHeight || 0;
  
  const handleCapture = async () => {
    pauseStreaming();
    setIsUploading(true);
    try {

    const tCaptureStart = Date.now();
    const capturedFile: File = await captureHighResFrame();
    const tUploadStart = Date.now();

    const analyzePayload = new FormData();
    analyzePayload.append("inspection_id", inspectionId);
    analyzePayload.append("file", capturedFile);
    activeQuestionIds.forEach(qId => {
      analyzePayload.append("question_ids", qId);
    });
    if (scannedBarcode !== null && scannedBarcode !== undefined) {
      analyzePayload.append("object_id", scannedBarcode);
    }
    await dispatch(analyzeEvidenceAsync(analyzePayload)).unwrap();
    const tUploadEnd = Date.now();
    captureTimingLogger.recordSuccess({
      ...buildSuccessParams("manual"),
      manualCaptureFrameMs: tUploadStart - tCaptureStart,
      manualUploadMs: tUploadEnd - tUploadStart,
    });
    setShowCameraControls(false);
    onCapture();
  } catch (error) {
      console.error("Capture failed:", error);
      resumeStreaming(); // Resume so the user can retry
  } finally {
      setIsUploading(false);
  }
};

  return (
    <Column className="camera-frame-viewport pwa-fullscreen-viewport">
      {/* Display low-res video directly (high-res hidden) */}
      {(
        <>
          <video
            ref={lowResVideoRef}
            className="image-source-video"
            autoPlay
            muted
            playsInline
          />
          <video
            ref={highResVideoRef}
            autoPlay
            muted
            playsInline
            style={{ display: 'none' }}
          />
        </>
      )}
      
      <div className="camera-frame-wrapper">
        {(
          <Column className="camera-frame">
            <Column className="top-line-camera-frame" />
            <Column className="left-line-camera-frame" />
            <Column className="right-line-camera-frame" />
            <Column className="bottom-line-camera-frame" />
          </Column>
        )}
        
      {/* Detection Overlay */}
      {lowResVideoRef.current && (
        <DetectionOverlay 
          detectionBoxes={detectionBoxes}
          videoWidth={videoWidth}
          videoHeight={videoHeight}
          imageNextStage={imageNextStage}
        />
      )}
      </div>

      {/* Guidance Message Overlay */}
      <GuidanceMessageOverlay raised={showCameraControls}/>
      
      {/* Dev Mode Controls */}
      {streamingDevMode && streamingService && (
        <StreamingDevControls streamingService={streamingService} />
      )}
      
      {/* Connection Status */}
      {streamingDevMode && (
        <div className={`connection-status ${streamingConnected ? 'connected' : 'disconnected'}`}>
          {streamingConnected ? 
            (streamingService?.isStreamingPaused() ? 'Connected (Paused)' : 'Connected') : 
            'Connecting...'}
        </div>
      )}

      {/* Upload spinner */}
      {isUploading && (
        <div className="capture-button__wrapper">
          <div className="capture-button" style={{ background: 'none' }}>
            <Loader size="large" type="circular" />
          </div>
        </div>
      )}
      
      {/* Camera controls */}
      {showCameraControls && !isUploading && (
        <>
          <CaptureButton
            onClick={handleCapture}
          />
          <div className="flashlight-button-position">
            <FlashlightButton
              videoRef={lowResVideoRef}
            />
          </div>
          <ZoomControl 
            videoRef={lowResVideoRef}
          />
        </>
      )}
      {!isUploading && (
        <CameraControlsButton
          direction={showCameraControls ? "down" : "up"}
          onClick={() => setShowCameraControls(!showCameraControls)}
        />
      )}
    </Column>
  );
};

export default CameraFrame;
